import { useEffect, useState, useCallback, useMemo } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Buildings, MagnifyingGlass } from "@phosphor-icons/react";
import AssignEntitiesDialog, { regLabel } from "./AssignEntitiesDialog";

// Super-admin: assign any user to any registration across all tenants.
export default function ConsoleUsers() {
  const [users, setUsers] = useState([]);
  const [regs, setRegs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [entityUser, setEntityUser] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [u, r] = await Promise.all([api.get("/users"), api.get("/registrations")]);
      setUsers(u.data || []);
      setRegs(r.data.registrations || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not load users");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const regName = (id) => { const r = regs.find((x) => x.id === id); return r ? regLabel(r) : id; };
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return users.filter((u) => !s || (u.name || "").toLowerCase().includes(s) || (u.email || "").toLowerCase().includes(s));
  }, [users, q]);

  return (
    <div className="space-y-4" data-testid="console-users-panel">
      <div>
        <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2"><Buildings size={20} weight="duotone" /> Users & Entity Assignment</h2>
        <p className="text-sm text-slate-500 mt-1">Assign any user to one or more registered entities across all tenants.</p>
      </div>
      <div className="relative max-w-sm">
        <MagnifyingGlass size={15} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or email"
          className="rounded-none pl-7" data-testid="console-users-search" />
      </div>

      {loading ? <div className="text-sm text-slate-500" data-testid="console-users-loading">Loading…</div> : (
        <div className="border border-slate-200 bg-white">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase text-slate-500">
              <th className="px-4 py-3">Name</th><th className="px-4 py-3">Email</th><th className="px-4 py-3">Role</th>
              <th className="px-4 py-3">Entities</th><th className="px-4 py-3"></th>
            </tr></thead>
            <tbody>
              {filtered.map((u) => (
                <tr key={u.id} className="border-b border-slate-100" data-testid={`console-user-row-${u.id}`}>
                  <td className="px-4 py-2.5 font-medium">{u.name}{u.is_superadmin && <span className="ml-1 text-[10px] text-emerald-700">(Claro)</span>}</td>
                  <td className="px-4 py-2.5 text-slate-500">{u.email}</td>
                  <td className="px-4 py-2.5 capitalize">{u.role}</td>
                  <td className="px-4 py-2.5">
                    <div className="flex flex-wrap items-center gap-1 max-w-[320px]">
                      {(u.allowed_registration_ids || []).map((rid) => regName(rid)).sort((a, b) => String(a).localeCompare(String(b))).map((nm, i) => (
                        <span key={i} className="inline-block border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-[10px] font-medium">{nm}</span>
                      ))}
                      {!(u.allowed_registration_ids || []).length && <span className="text-[10px] text-slate-400 italic">none</span>}
                    </div>
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <Button size="sm" variant="outline" className="rounded-none h-8 text-xs" disabled={u.is_superadmin}
                      onClick={() => setEntityUser(u)} data-testid={`console-edit-entities-${u.id}`}>Edit entities</Button>
                  </td>
                </tr>
              ))}
              {!filtered.length && <tr><td colSpan={5} className="px-4 py-6 text-center text-slate-400" data-testid="console-users-empty">No users match.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      <AssignEntitiesDialog open={!!entityUser} onOpenChange={(o) => !o && setEntityUser(null)}
        user={entityUser} registrations={regs} onSaved={load} />
    </div>
  );
}
